import React from "react";
import NodePorts from "./NodePorts";
import { Handle, Position } from "react-flow-renderer";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, Chip } from "@material-ui/core";
import { TouchApp } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  nodeContainer: {
    minWidth: 200,
    maxWidth: 280,
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    background: "linear-gradient(135deg, #075e54 0%, #128c7e 100%)",
    boxShadow: "0 4px 12px rgba(7, 94, 84, 0.45)",
    border: "2px solid #075e54",
    color: "white",
    position: "relative",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(7, 94, 84, 0.55)",
    },
  },
  nodeHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  nodeLabel: {
    fontWeight: 700,
    fontSize: 13,
    color: "white",
    letterSpacing: 0.3,
  },
  badge: {
    fontSize: 9,
    fontWeight: 700,
    backgroundColor: "#25d366",
    color: "#000",
    padding: "1px 5px",
    borderRadius: 4,
    marginLeft: 4,
    letterSpacing: 0.5,
  },
  nodeContent: {
    fontSize: 12,
    opacity: 0.95,
    padding: theme.spacing(0.75, 1),
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    borderRadius: 6,
    marginBottom: theme.spacing(0.75),
  },
  buttonsArea: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  buttonChip: {
    fontSize: 10,
    fontWeight: 600,
    height: 22,
    borderRadius: 11,
    color: "white",
  },
  replyChip: {
    backgroundColor: "rgba(37, 211, 102, 0.3)",
    border: "1px solid #25d366",
  },
  urlChip: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    border: "1px solid rgba(255,255,255,0.4)",
  },
  handle: {
    width: 12,
    height: 12,
    background: "#25d366",
    border: "2px solid #075e54",
    borderRadius: "50%",
  },
  handleLabel: {
    position: "absolute",
    bottom: -18,
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: 9,
    fontWeight: 600,
    color: "#075e54",
    backgroundColor: "white",
    padding: "1px 4px",
    borderRadius: 4,
    whiteSpace: "nowrap",
    maxWidth: 72,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const ButtonsNode = ({ data, selected }) => {
  const classes = useStyles();
  const messageText = data?.message || data?.text || data?.label || "Botones WA";
  const buttons = data?.buttons || [];

  // Solo los botones tipo "reply" crean handles de salida (para ruteo de flujo)
  const replyButtons = buttons.filter((b) => !b.type || b.type === "reply");
  const otherButtons = buttons.filter((b) => b.type && b.type !== "reply");

  const getHandlePosition = (index, total) => {
    if (total <= 1) return "50%";
    const spacing = 100 / (total + 1);
    return `${spacing * (index + 1)}%`;
  };

  return (
    <Box
      className={classes.nodeContainer}
      style={{
        boxShadow: selected
          ? "0 6px 20px rgba(7, 94, 84, 0.7)"
          : "0 4px 12px rgba(7, 94, 84, 0.45)",
      }}
    >
      <NodePorts color="#075e54" salida={false} />
      {/* Handle de entrada */}
      {/* Cabecera */}
      <Box className={classes.nodeHeader}>
        <TouchApp style={{ fontSize: 18, color: "#25d366" }} />
        <Typography className={classes.nodeLabel}>Botones WA</Typography>
        <span className={classes.badge}>NATIVO</span>
      </Box>

      {/* Texto del mensaje */}
      <Box className={classes.nodeContent}>
        {messageText.length > 45
          ? `${messageText.substring(0, 45)}…`
          : messageText}
      </Box>

      {/* Preview de botones */}
      {buttons.length > 0 && (
        <Box className={classes.buttonsArea}>
          {replyButtons.map((btn, i) => (
            <Chip
              key={`reply-${i}`}
              label={`↩ ${btn.text || `Botón ${i + 1}`}`}
              size="small"
              className={`${classes.buttonChip} ${classes.replyChip}`}
            />
          ))}
          {otherButtons.map((btn, i) => (
            <Chip
              key={`other-${i}`}
              label={`${btn.type === "url" ? "🔗" : btn.type === "copy" ? "📋" : "📞"} ${btn.text || "Botón"}`}
              size="small"
              className={`${classes.buttonChip} ${classes.urlChip}`}
            />
          ))}
        </Box>
      )}

      {buttons.length === 0 && (
        <Typography style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
          Sin botones configurados
        </Typography>
      )}

      {/* Handles de salida — uno por cada botón reply */}
      {replyButtons.length > 0 ? (
        replyButtons.map((btn, index) => {
          const handleId = btn.id || btn.text || `btn_${index}`;
          const pos = getHandlePosition(index, replyButtons.length);
          return (
            <Handle
              key={`src-${handleId}`}
              type="source"
              position={Position.Bottom}
              id={handleId}
              className={classes.handle}
              style={{ bottom: -6, left: pos, transform: "translateX(-50%)" }}
            >
              <Box className={classes.handleLabel} title={btn.text}>
                {(btn.text || handleId).length > 10
                  ? `${(btn.text || handleId).substring(0, 10)}…`
                  : btn.text || handleId}
              </Box>
            </Handle>
          );
        })
      ) : (
        <Handle
          type="source"
          position={Position.Bottom}
          className={classes.handle}
          style={{ bottom: -6 }}
        />
      )}
    </Box>
  );
};

export default ButtonsNode;
